/* eslint-disable @typescript-eslint/no-unused-vars */
import "./UsagePage.scss";

import { Card, CardBody, CardHeader } from "@flick/fep-library";
import { AggregatedUsageSection } from "components/AggregatedUsageSection";
import { ratingQueryDateRange } from "components/CurrentBillPeriodSection/CurrentBillPeriod.utils";
import { HomeGenerationSection } from "components/HomeGenerationCharts";
import { LoadingSection } from "components/LoadingSection";
import { CsvDownloader } from "components/Usage/CsvDownloader";
import { NoAvailableData } from "components/Usage/NoAvailableData";
import { UsageClosedAccountDefault } from "components/Usage/UsageClosedAccountDefault";
import { LoadingPage } from "pages/LoadingPage";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "reduxUtils/hook";
import { isUserHavingExportChannel } from "selectors/currentRateCardSelector";
import {
  calculateCurrentPeriodDates,
  calculatePreviousPeriodDates,
  dateUsageDataAvailableFrom,
  previousDataAvailable,
  yearAgoDataAvailable,
} from "utils/aggregateCalculations/calculatePeriodDates";
import {
  AVAILABLE_PERIODS,
  PERIOD_CODES,
  PRODUCT_DISPLAY_NAMES,
} from "utils/constants";
import { jwtClient } from "utils/jwtClient";

import { DaysProgressBar } from "./components/DaysProgressBar";
import {
  LargeTab,
  LargeTabList,
  LargeTabPanel,
} from "./components/LargeTabPanel/LargeTabPanel";
import { LargeTabProvider } from "./components/LargeTabPanel/LargeTabProvider";
import { TabProvider } from "./components/TabPanel/TabContext";
import { Tab, TabList, TabPanel } from "./components/TabPanel/TabPanel";
import { CurrentBillingPeriodCounting } from "./compositions/CurrentBillingPeriodCounting";
import { PowerOrSolarBreakDown } from "./compositions/PowerOrSolarBreakdown";
import { SolarTotalSoldAndBought } from "./compositions/SolarTotalSoldAndBought";
import { TotalUsageSection } from "./compositions/TotalUsageSection";

export const UsagePage = () => {
  const { supplyNodeRef, nativeRenderMode, active, accountNumber } =
    useAppSelector((store) => store.currentAccount);
  const currentProduct = useAppSelector((store) => store.currentProduct);
  const { billingEntityData, currentBillingPeriod } = useAppSelector(
    (store) => store.billingEntityData,
  );

  const currentDate = new Date();
  const isSolar = useAppSelector(isUserHavingExportChannel);

  if (accountNumber && !active) return <UsageClosedAccountDefault />;
  if (!accountNumber && !active) return <NoAvailableData />;
  if (!billingEntityData || !currentBillingPeriod || isSolar === undefined)
    return <LoadingPage />;

  const CSVDownload = ({ viewport }) => {
    return (
      <div className={`spacer--large csv_download--${viewport}`}>
        <Card cardPaddingType="compact">
          <CardBody>
            <CsvDownloader
              viewport={viewport}
              billingEntityStartAt={billingEntityData?.start_at}
              billingEntityEndAt={billingEntityData?.end_at}
              currentPeriodEndDate={currentDate}
            />
          </CardBody>
        </Card>
      </div>
    );
  };

  const CurrentBillPeriod = () => {
    return (
      <>
        <CurrentBillingPeriodCounting
          startAt={currentBillingPeriod.startAt}
          endAt={currentBillingPeriod.endAt}
        />
        <DaysProgressBar
          currentDate={currentDate}
          currentBillingPeriodEndAt={currentBillingPeriod.endAt}
          duration={currentBillingPeriod.duration}
        />
      </>
    );
  };

  /**
   * in term of no solar, we still need large tab provider
   * since we use the useLargeTabContext() in <PowerOrSolarBreakDown/> children
   */
  if (!isSolar) {
    return (
      <div className="usage_page">
        <LargeTabProvider value="usage">
          <section className="page_section page_section--top">
            <section className="page_section__column page_section__column--1">
              <CurrentBillPeriod />
              <div className="spacer--large" />
              <TotalUsageSection componentTitle="Current Usage So Far" />
              {billingEntityData && !nativeRenderMode && (
                <CSVDownload viewport="desktop" />
              )}
              <div className="spacer--large" />
            </section>
            <section className="page_section__column page_section__column--2">
              <PowerOrSolarBreakDown />
            </section>
          </section>
        </LargeTabProvider>
        {billingEntityData && !nativeRenderMode && (
          <CSVDownload viewport="mobile" />
        )}
      </div>
    );
  }

  return (
    <div className="usage_page">
      <LargeTabProvider value="solar">
        <section className="page_section page_section--top">
          <section className="page_section__column page_section__column--1 background--grey">
            {/* Tab List */}
            <LargeTabList>
              <LargeTab label="Solar" value="solar" />
              <LargeTab label="Usage" value="usage" />
            </LargeTabList>

            <div className="spacer--large" />
            {/* only use Large Tab Panel view for non-shared compositions */}
            <LargeTabPanel value="solar">
              <h2 className="section-title heading h4">Your Solar</h2>
            </LargeTabPanel>
            <LargeTabPanel value="usage">
              <h2 className="section-title heading h4">Your Usage</h2>
            </LargeTabPanel>

            <div className="spacer--medium" />
            <CurrentBillPeriod />
            <div className="spacer--large" />

            {/* only use Large Tab Panel view for non-shared compositions */}
            <LargeTabPanel value="solar">
              <SolarTotalSoldAndBought componentTitle="Current Sold & Bought So Far" />
            </LargeTabPanel>
            <LargeTabPanel value="usage">
              <TotalUsageSection componentTitle="Current Usage So Far" />
            </LargeTabPanel>

            {billingEntityData && !nativeRenderMode && (
              <CSVDownload viewport="desktop" />
            )}
            <div className="spacer--large" />
          </section>
          <section className="page_section__column page_section__column--2">
            <PowerOrSolarBreakDown />
          </section>
        </section>
      </LargeTabProvider>
      {billingEntityData && !nativeRenderMode && (
        <CSVDownload viewport="mobile" />
      )}
    </div>
  );
};
